import React, { useMemo, useState, useEffect } from "react";
import { Table, Tooltip, Button, Space, Spin, Tag, Badge } from "antd";
import {
    LeftOutlined,
    RightOutlined,
    HomeOutlined,
    LaptopOutlined,
    MedicineBoxOutlined,
    CarOutlined,
    HomeOutlined as HomeIcon,
    FieldTimeOutlined,
    BankOutlined,
    CoffeeOutlined,
    MinusCircleOutlined,
    AlertOutlined,
    HeartOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useTranslation } from "react-i18next";
import { getColorFromStatus, getTextColorForStatus, dayStatuses } from "../../core/utils/weekPlanUtils";
import styles from './MonthlyScheduleTable.module.css';

// Extend dayjs with isoWeek plugin
dayjs.extend(isoWeek);

// Status icon mapping for better visual recognition
const getStatusIcon = (status) => {
    const iconMap = {
        'Work': <LaptopOutlined />,           // Laptop for work/office
        'Sick': <MedicineBoxOutlined />,      // Medicine box for illness
        'Holiday': <CarOutlined />,           // Car for holiday/vacation
        'Vacation': <CarOutlined />,          // Car for holiday/vacation
        'TimeCompensation': <FieldTimeOutlined />, // Time field for compensation
        'ChildSick': <HeartOutlined />,       // Heart for child care
        'PublicHoliday': <BankOutlined />,    // Bank/building for public holiday
        'Weekend': <CoffeeOutlined />,        // Coffee for rest/weekend
        'Off': <MinusCircleOutlined />,       // Minus circle for off/inactive
        'EmergencyService': <AlertOutlined />, // Alert for emergency
    };
    return iconMap[status] || null;
};

// Get abbreviated status text (max 3 chars)
const getStatusAbbrev = (status, t) => {
    const translated = t(`statuses.${status?.toLowerCase()}`);
    return translated?.substring(0, 3).toUpperCase() || '';
};

const MonthlyScheduleTable = ({ dataSource, updateMonthPlan, selectedMonth, selectedYear, loading = false }) => {
    const { t } = useTranslation();
    const daysInMonth = dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth();
    const [currentDate, setCurrentDate] = useState(dayjs(`${selectedYear}-${selectedMonth}-01`));
    const [isAnimating, setIsAnimating] = useState(false);
    
    // Update currentDate when props change
    useEffect(() => {
        setCurrentDate(dayjs(`${selectedYear}-${selectedMonth}-01`));
    }, [selectedYear, selectedMonth]);
    const handlePrevMonth = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentDate(prev => prev.subtract(1, "month"));
            let month = selectedMonth - 1;
            let year = selectedYear;
            if (month < 1) {
                month = 12;
                year -= 1;
            }
            updateMonthPlan(month, year);
            setIsAnimating(false);
        }, 150);
    };

    const handleNextMonth = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setCurrentDate(prev => prev.add(1, "month"));
            let month = selectedMonth + 1;
            let year = selectedYear;
            if (month > 12) {
                month = 1;
                year += 1;
            }
            updateMonthPlan(month, year);
            setIsAnimating(false);
        }, 150);
    };
    
    const handleCurrentMonth = () => {
        setIsAnimating(true);
        setTimeout(() => {
            const now = dayjs();
            setCurrentDate(now.startOf('month'));
            updateMonthPlan(now.month() + 1, now.year());
            setIsAnimating(false);
        }, 150);
    };

    // Calculate summary statistics for each employee
    const calculateSummary = (record) => {
        if (!record?.schedule) return { work: 0, holiday: 0, sick: 0, publicholiday: 0, other: 0 };

        const summary = { work: 0, holiday: 0, sick: 0, publicholiday: 0, other: 0 };

        Object.values(record.schedule).forEach(entry => {
            const status = entry?.status?.toLowerCase();
            if (status === 'work') summary.work++;
            else if (status === 'holiday' || status === 'vacation') summary.holiday++; // Combine holiday and vacation
            else if (status === 'sick' || status === 'childsick') summary.sick++;
            else if (status === 'publicholiday') summary.publicholiday++;
            else if (status) summary.other++;
        });

        return summary;
    };

    const columns = useMemo(() => {
        const today = dayjs();
        let lastWeekNum = null;

        const dayColumns = Array.from({ length: daysInMonth }, (_, i) => {
            const dayNumber = i + 1;
            const currentDay = dayjs(`${selectedYear}-${selectedMonth}-${dayNumber}`);
            const dateKey = currentDay.format("YYYY-MM-DD");
            const isToday = currentDay.isSame(today, 'day');
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
            const dayName = t(`days.${currentDay.format('dddd').toLowerCase()}`).substring(0, 2);
            const weekNum = currentDay.isoWeek();
            const isNewWeek = lastWeekNum !== null && weekNum !== lastWeekNum;
            const isMonday = currentDay.day() === 1;
            lastWeekNum = weekNum;

            return {
                title: (
                    <div className={`${styles.dayHeader} ${isToday ? styles.todayHeader : ''} ${isWeekend ? styles.weekendHeader : ''}`}>
                        {isMonday && (
                            <div className={styles.weekNumber}>{t('calendarWeek')}{weekNum}</div>
                        )}
                        <div className={styles.dayNumber}>{dayNumber}</div>
                        <div className={styles.dayName}>{dayName}</div>
                        {isToday && <div className={styles.todayBadge}>{t('today') || 'Today'}</div>}
                    </div>
                ),
                key: `day-${dayNumber}`,
                width: isMonday ? 60 : 50,
                className: `${isWeekend ? styles.weekendColumn : ''} ${isMonday ? styles.weekStartColumn : ''} ${isToday ? styles.todayColumn : ''}`,
                render: (_, record) => {
                    const entry = record.schedule?.[dateKey];
                    const status = entry?.status;

                    if (status) {
                        const bgColor = getColorFromStatus(status);
                        const textColor = getTextColorForStatus(status);
                        const statusClass = `status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`;
                        const statusIcon = getStatusIcon(status);

                        return (
                            <Tooltip
                                title={
                                    <div className={styles.statusTooltip}>
                                        <strong>{record.userName}</strong><br/>
                                        <span>{currentDay.format('dddd')}, {currentDay.format('DD.MM.YYYY')}</span><br/>
                                        <span>{t('status')}: {t(`statuses.${status?.toLowerCase()}`)}</span>
                                    </div>
                                }
                                placement="top"
                            >
                                <div className={`${styles.dayCell} ${styles[statusClass] || ''} ${isToday ? styles.todayCellHighlight : ''}`}>
                                    <div
                                        className={styles.statusIndicator}
                                        style={{ backgroundColor: bgColor, color: textColor }}
                                    >
                                        <span className={styles.statusIcon} style={{ color: textColor }}>{statusIcon}</span>
                                        <span className={styles.statusText} style={{ color: textColor }}>{getStatusAbbrev(status, t)}</span>
                                    </div>
                                    {isToday && <div className={styles.todayIndicator} />}
                                </div>
                            </Tooltip>
                        );
                    }

                    return (
                        <div className={`${styles.dayCell} ${styles.emptyDay} ${isWeekend ? styles.weekendDay : ''} ${isToday ? styles.todayCellHighlight : ''}`}>
                            {isToday && <div className={styles.todayIndicator} />}
                        </div>
                    );
                },
                onCell: () => ({
                    style: {
                        padding: 0,
                        margin: 0,
                        height: "100%",
                    },
                }),
            };
        });

        // Summary column showing statistics
        const summaryColumn = {
            title: (
                <div className={styles.summaryHeader}>
                    {t('summary') || 'Summary'}
                </div>
            ),
            key: 'summary',
            fixed: 'right',
            width: 140,
            className: styles.summaryColumn,
            render: (_, record) => {
                const stats = calculateSummary(record);
                return (
                    <div className={styles.summaryCell}>
                        <Tooltip title={t('statuses.work')}>
                            <Tag color="success" className={styles.summaryTag}>{stats.work}</Tag>
                        </Tooltip>
                        <Tooltip title={t('statuses.holiday')}>
                            <Tag color="purple" className={styles.summaryTag}>{stats.holiday}</Tag>
                        </Tooltip>
                        <Tooltip title={t('statuses.sick')}>
                            <Tag color="error" className={styles.summaryTag}>{stats.sick}</Tag>
                        </Tooltip>
                    </div>
                );
            },
        };

        return [
            {
                title: (
                    <div className={styles.nameColumnHeader}>
                        {t("name")}
                    </div>
                ),
                dataIndex: "userName",
                key: "userName",
                fixed: "left",
                width: 140,
                className: styles.nameColumn,
                render: (name, record) => (
                    <div className={styles.employeeName}>
                        <span className={styles.employeeNameText}>{name}</span>
                    </div>
                ),
            },
            ...dayColumns,
            summaryColumn,
        ];
    }, [daysInMonth, selectedMonth, selectedYear, t]);

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spin size="large" className={styles.loadingSpinner} />
                <p style={{ marginTop: 16, color: '#666' }}>{t('loadingMonthlyData')}...</p>
            </div>
        );
    }

    // Inline legend for quick reference
    const renderLegend = () => (
        <div className={styles.inlineLegend}>
            <span className={styles.legendLabel}>{t('legende')}:</span>
            <div className={styles.legendItems}>
                {dayStatuses.map((status) => (
                    <Tooltip key={status} title={t(`statuses.${status?.toLowerCase()}`)}>
                        <Tag
                            className={styles.legendItem}
                            style={{
                                backgroundColor: getColorFromStatus(status),
                                color: getTextColorForStatus(status),
                                border: `1px solid ${getTextColorForStatus(status)}20`,
                            }}
                        >
                            {getStatusIcon(status)} {getStatusAbbrev(status, t)}
                        </Tag>
                    </Tooltip>
                ))}
            </div>
        </div>
    );

    // Employee count info
    const employeeCount = dataSource?.length || 0;

    return (
        <div className={styles.monthlyTableContainer}>
            {/* Enhanced Header with Navigation */}
            <div className={styles.monthlyHeader}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.monthlyTitle}>
                        {t('monthlySchedule')}
                    </h2>
                    <Badge count={employeeCount} className={styles.employeeCount} showZero>
                        <span className={styles.employeeLabel}>{t('employees')}</span>
                    </Badge>
                </div>

                <div className={styles.navigationControls}>
                    <Tooltip title={t('previousMonth')}>
                        <Button
                            className={styles.navButton}
                            icon={<LeftOutlined />}
                            onClick={handlePrevMonth}
                            loading={isAnimating}
                        />
                    </Tooltip>

                    <div className={styles.currentMonth}>
                        <span className={styles.monthName}>{currentDate.format("MMMM")}</span>
                        <span className={styles.yearName}>{currentDate.format("YYYY")}</span>
                    </div>

                    <Tooltip title={t('currentMonth')}>
                        <Button
                            className={styles.navButton}
                            icon={<HomeIcon />}
                            onClick={handleCurrentMonth}
                            loading={isAnimating}
                        />
                    </Tooltip>

                    <Tooltip title={t('nextMonth')}>
                        <Button
                            className={styles.navButton}
                            icon={<RightOutlined />}
                            onClick={handleNextMonth}
                            loading={isAnimating}
                        />
                    </Tooltip>
                </div>
            </div>

            {/* Inline Legend */}
            {renderLegend()}

            {/* Enhanced Table */}
            <div className={styles.modernMonthlyTable}>
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="userId"
                    pagination={false}
                    scroll={{ x: "max-content", y: 600 }}
                    bordered={false}
                    size="small"
                    loading={isAnimating}
                    locale={{
                        emptyText: (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateIcon}>📊</div>
                                <div className={styles.emptyStateText}>{t('noMonthlyData')}</div>
                                <div className={styles.emptyStateSubtext}>{t('selectDifferentMonth')}</div>
                            </div>
                        )
                    }}
                    style={{ width: "100%" }}
                />
            </div>
        </div>
    );
};

export default MonthlyScheduleTable;
